import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User, Plus, Check, X, ChevronUp, ChevronDown, Search, ArrowRight, Loader, BarChart2, CheckCircle, Calendar, LogIn, LogOut, GripVertical, Archive, FileText, RotateCcw, Inbox, Trash2, Users } from 'lucide-react';
import { neonQuery } from '../services/api';
import { msGraphLogin, msGraphLogout, checkExistingSession, fetchCalendarEvents, onMsLoginComplete } from '../services/msGraph';
import TodoProjects from './TodoProjects';
import QuickCapture from '../components/QuickCapture';
import FrogCard from '../components/FrogCard';
import FrogWeek from '../components/FrogWeek';
import TodayBoard, { call as boardCall } from '../components/TodayBoard';

// A calendar location is sometimes a place and sometimes a meeting link. Outlook and
// Google both drop the whole join URL in there, and a long unbreakable string forces
// its day column wide, which is what blew Friday out. Never render the URL itself: a
// short Join link is more use anyway.
// Work handed to someone else is not his work any more. It keeps its date so the week
// still reads truthfully, but it must leave Today, Up Next and the percentage, or
// assigning something away changes nothing on the screen he actually looks at.
function isMineToDo(t) {
  const o = String(t.owner || "").trim().toLowerCase();
  return !o || o === "greg";
}

function splitLocation(loc) {
  const raw = String(loc || "").trim();
  if (!raw) return null;
  const found = raw.match(/https?:[^\s<>"]+/);
  const text = raw.replace(/https?:[^\s<>"]+/g, " ").replace(/[\s,;|>-]+$/, "").trim();
  if (!found && !text) return null;
  return { text, url: found ? found[0] : null };
}

// Placement buttons in the capture inbox. Big enough to hit on a phone.
function placeBtn(primary) {
  return {
    padding: "5px 12px",
    border: primary ? "1px solid var(--navy)" : "1px solid var(--gray-300)",
    background: primary ? "var(--navy)" : "#fff",
    color: primary ? "#fff" : "#3d4b58",
    borderRadius: 14,
    fontSize: 12,
    fontWeight: primary ? 700 : 600,
    cursor: "pointer",
    fontFamily: "inherit",
  };
}

function getToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD in ET
}

// Habits run until 2 AM (Greg, 2026-09-30: "I don't always go to bed by midnight"), so the
// daily list is the Eastern date of (now - 2 hours). Matches habitDay() in today-board.js.
function getHabitDay() {
  return new Date(Date.now() - 2 * 3600 * 1000).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
}

function getSunday(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  d.setDate(d.getDate() - d.getDay()); // getDay() 0=Sun, so this goes back to Sunday
  return d.toISOString().slice(0, 10);
}

function formatDay(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' });
}

// Calendar events to hide (daily reminders, etc.)
const HIDDEN_EVENTS = [
  '5 min relax & read - be great',
];

function formatEventTime(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : 'Z'));
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/New_York' }).toLowerCase();
}

function getEventMinutes(isoStr) {
  if (!isoStr) return 0;
  const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : 'Z'));
  const h = parseInt(d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: false, timeZone: 'America/New_York' }));
  const m = d.getMinutes();
  return h * 100 + m;
}

function getEventDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + (isoStr.endsWith('Z') ? '' : 'Z'));
  // Convert to ET date string
  return d.toLocaleDateString('en-CA', { timeZone: 'America/New_York' }); // YYYY-MM-DD
}

const SECTION_STYLE = { marginBottom: 24 };
const INPUT_STYLE = { flex: 1, padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', outline: 'none' };
const BTN_STYLE = (color, disabled) => ({
  display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px',
  background: disabled ? 'var(--gray-300)' : color, color: '#fff', border: 'none', borderRadius: 6,
  fontSize: 12, fontWeight: 600, cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit',
});

export default function TodoGreg({ navigateTo }) {
  const today = getToday();
  const habitDay = getHabitDay();
  const [activeTab, setActiveTab] = useState('today');

  // ── quick capture ─────────────────────────────────────────────────────────
  // A thought caught on the phone lands here untriaged: on nobody list and in
  // nobody brief until it is placed below. Capture asks for ONE field on purpose.
  // No voice code: webkitSpeechRecognition is Chrome and Edge only and does not
  // work in an iOS PWA, and the iOS keyboard microphone already does the job on a
  // focused text box.
  const [inbox, setInbox] = useState([]);
  const [owners, setOwners] = useState([]);
  const [placing, setPlacing] = useState(null);

  const loadInbox = useCallback(async () => {
    try {
      const rows = await neonQuery("greg_inbox_list");
      setInbox(Array.isArray(rows) ? rows : []);
    } catch (e) { /* the page is still usable without it */ }
  }, []);

  useEffect(() => { loadInbox(); }, [loadInbox]);
  useEffect(() => {
    let live = true;
    neonQuery("work_owners_list")
      .then((r) => { if (live && Array.isArray(r)) setOwners(r.filter((o) => o.kind === "person")); })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const place = async (id, horizon, owner) => {
    setPlacing(id);
    try {
      await neonQuery("greg_inbox_place", { id, horizon, owner: owner || "", date: today });
      await loadInbox();
      if (horizon === "today") loadWeekly();
    } catch (e) { /* leave it in the inbox rather than losing it */ }
    finally { setPlacing(null); }
  };

  const binCapture = async (id) => {
    setPlacing(id);
    try { await neonQuery("greg_inbox_bin", { id }); await loadInbox(); }
    catch (e) { /* leave it */ }
    finally { setPlacing(null); }
  };

  // ── Daily state ──
  const [dailyItems, setDailyItems] = useState([]);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [newDaily, setNewDaily] = useState('');
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [stepCount, setStepCount] = useState(null);
  const [dailyDragIdx, setDailyDragIdx] = useState(null);
  // What is on today's board (Everything for today + Upcoming), as 'srcTable srcId'. Greg,
  // 2026-09-29: a thing lives in ONE of Everything for today, Upcoming, or Tasks - so anything
  // on the board is left out of the Tasks list and the Backlog below.
  const [boardKeys, setBoardKeys] = useState(() => new Set());
  const onBoard = (table, id) => boardKeys.has(table + ' ' + id);
  // Bumped to make the board re-read after something is put on it from the Tasks list.
  const [boardRefresh, setBoardRefresh] = useState(0);
  const [frogRefresh, setFrogRefresh] = useState(0);

  // ── Weekly state ──
  const [weekStart, setWeekStart] = useState(getSunday(today));
  const [weeklyTasks, setWeeklyTasks] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(true);
  const [newWeekly, setNewWeekly] = useState('');
  const [newWeeklyDay, setNewWeeklyDay] = useState(today);

  // ── Backlog state ──
  const [backlog, setBacklog] = useState([]);
  const [backlogLoading, setBacklogLoading] = useState(true);
  const [backlogSearch, setBacklogSearch] = useState('');
  const [newBacklog, setNewBacklog] = useState('');

  // ── Projects state ──
  const [projectsData, setProjectsData] = useState({ projects: [], tasks: [] });

  // ── Calendar state ──
  const [msAccount, setMsAccount] = useState(null);
  const [calEvents, setCalEvents] = useState([]);
  const [calLoading, setCalLoading] = useState(false);
  const [dismissedEvents, setDismissedEvents] = useState([]);
  const [queue, setQueue] = useState([]); // [{item_key, sort_order}]
  const [queueLoaded, setQueueLoaded] = useState(false);
  const [dragItem, setDragItem] = useState(null);
  const [frogKey, setFrogKey] = useState(null);
  // Per-item notes. The tile says "Text the electrician"; the note says what to tell him.
  const [notes, setNotes] = useState({});        // { 'task-12': 'text' }
  const [noteOpen, setNoteOpen] = useState(null); // item.key currently expanded
  const [noteDraft, setNoteDraft] = useState('');
  // The week grid shows compact tiles; the whole detail lives behind a click.
  const [detailTask, setDetailTask] = useState(null);
  const [detailDraft, setDetailDraft] = useState('');

  // ── Loaders ──
  const loadDaily = useCallback(async () => {
    try {
      const rows = await neonQuery('greg_daily_today', { date: habitDay });
      setDailyItems(rows);
    } catch (e) { console.error(e); }
    finally { setDailyLoading(false); }
  }, [habitDay]);

  const loadHistory = useCallback(async () => {
    try {
      const rows = await neonQuery('greg_daily_history');
      setHistory(rows);
    } catch (e) { console.error(e); }
  }, []);

  const loadWeekly = useCallback(async () => {
    try {
      const rows = await neonQuery('greg_weekly_tasks', { weekStart });
      setWeeklyTasks(rows);
    } catch (e) { console.error(e); }
    finally { setWeeklyLoading(false); }
  }, [weekStart]);

  const loadBacklog = useCallback(async () => {
    try {
      const rows = await neonQuery('greg_backlog_list', { search: backlogSearch || undefined });
      setBacklog(rows);
    } catch (e) { console.error(e); }
    finally { setBacklogLoading(false); }
  }, [backlogSearch]);

  const loadProjects = useCallback(async () => {
    try {
      const res = await neonQuery('greg_projects_overview');
      if (res && Array.isArray(res.projects)) setProjectsData(res);
    } catch (e) { console.error('Projects load error:', e); }
  }, []);

  const loadSteps = useCallback(async () => {
    try {
      const rows = await neonQuery('greg_steps_today', { date: today });
      setStepCount(rows?.[0]?.steps ?? 0);
    } catch (e) { console.error('Steps load error:', e); }
  }, [today]);

  useEffect(() => { loadDaily(); loadHistory(); loadSteps(); }, [loadDaily, loadHistory, loadSteps]);
  useEffect(() => { loadWeekly(); }, [loadWeekly]);
  useEffect(() => { loadBacklog(); }, [loadBacklog]);
  useEffect(() => { loadProjects(); }, [loadProjects]);

  // ── Daily surface tick ──
  // This WAS an auto-rollover that moved every past-due task to today. It is now a record
  // of the appearance, not a rewrite of the date: see the `greg_weekly_rollover` handler in
  // neon.js for why that one UPDATE was the reason this board could never be ranked.
  // Runs once per calendar day, only on the current week, and no longer needs a past-due
  // task to exist - the count has to accrue on quiet days too or avoidance reads as zero.
  const surfaceDoneRef = useRef(false);
  useEffect(() => {
    if (weeklyLoading) return;
    if (weekStart !== getSunday(today)) return;
    if (surfaceDoneRef.current) return;
    if (localStorage.getItem('greg_surface_last') === today) { surfaceDoneRef.current = true; return; }
    surfaceDoneRef.current = true;
    localStorage.setItem('greg_surface_last', today);
    neonQuery('greg_daily_surface', { to_date: today })
      .then(() => loadWeekly())
      .catch(e => console.error('Surface tick error:', e));
  }, [weeklyLoading, weekStart, today, loadWeekly]);

  // ── Calendar: check existing session on mount + listen for redirect login ──
  useEffect(() => {
    checkExistingSession().then(acct => { if (acct) setMsAccount(acct); });
    onMsLoginComplete(acct => setMsAccount(acct));
    neonQuery('cal_dismissed_list').then(rows => setDismissedEvents(rows)).catch(() => {});
    neonQuery('today_queue_load', { date: today }).then(rows => { setQueue(rows); setQueueLoaded(true); }).catch(() => setQueueLoaded(true));
  }, []);

  // Today's frog is the Frog week's (frog_picks), so the old per-row frog button and the strip
  // can never disagree. Re-read whenever either board changes.
  useEffect(() => {
    boardCall('frogs').then(w => {
      const f = w && w.tiles && w.tiles[0] && w.tiles[0].frog;
      setFrogKey(f && f.srcTable === 'greg_weekly_tasks' ? 'task-' + f.srcId : null);
    }).catch(() => {});
  }, [frogRefresh, boardRefresh]);

  // ── Calendar: fetch events when account or week changes ──
  const loadCalendar = useCallback(async () => {
    if (!msAccount) return;
    setCalLoading(true);
    try {
      const endDay = new Date(weekStart + 'T12:00:00');
      endDay.setDate(endDay.getDate() + 6);
      const events = await fetchCalendarEvents(weekStart, endDay.toISOString().slice(0, 10));
      setCalEvents(events);
    } catch (e) { console.error('Calendar load error:', e); }
    finally { setCalLoading(false); }
  }, [msAccount, weekStart]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  const handleMsLogin = async () => {
    await msGraphLogin();
  };

  const dismissEvent = async (eventId) => {
    setDismissedEvents(prev => [...prev, eventId]);
    try {
      const rows = await neonQuery('cal_dismiss', { event_id: eventId });
      setDismissedEvents(rows);
    } catch (e) { console.error('Dismiss error:', e); }
  };

  // Undo a dismissal — a meeting cleared by mistake comes straight back.
  const undismissEvent = async (eventId) => {
    setDismissedEvents(prev => prev.filter(id => id !== eventId));
    try {
      const rows = await neonQuery('cal_undismiss', { event_id: eventId });
      setDismissedEvents(rows);
    } catch (e) {
      console.error('Undismiss error:', e);
      neonQuery('cal_dismissed_list').then(rows => setDismissedEvents(rows)).catch(() => {});
    }
  };

  const handleMsLogout = async () => {
    await msGraphLogout();
    setMsAccount(null);
    setCalEvents([]);
  };

  // ── Daily actions ──
  const addDailyTemplate = async () => {
    const text = newDaily.trim();
    if (!text) return;
    setNewDaily('');
    await neonQuery('greg_daily_add_template', { text });
    loadDaily();
  };

  const removeDailyTemplate = async (id) => {
    await neonQuery('greg_daily_remove_template', { id });
    loadDaily();
  };

  const toggleDaily = async (id, completed) => {
    // Optimistic update
    setDailyItems(prev => prev.map(i => i.id === id ? { ...i, completed: !completed } : i));
    try {
      const rows = await neonQuery('greg_daily_toggle', { id, date: habitDay, completed: !completed });
      if (rows?.length > 0) setDailyItems(rows);
    } catch (e) {
      console.error('Daily toggle error:', e);
      loadDaily(); // revert on error
    }
  };

  const handleDailyDrop = async (dropIdx) => {
    if (dailyDragIdx === null || dailyDragIdx === dropIdx) { setDailyDragIdx(null); return; }
    const reordered = [...dailyItems];
    const [moved] = reordered.splice(dailyDragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setDailyItems(reordered);
    setDailyDragIdx(null);
    try {
      await neonQuery('greg_daily_reorder', { ids: reordered.map(i => i.id) });
      loadDaily();
    } catch (e) { console.error('Reorder error:', e); loadDaily(); }
  };

  // ── Weekly actions ──
  const addWeeklyTask = async () => {
    const text = newWeekly.trim();
    if (!text) return;
    setNewWeekly('');
    await neonQuery('greg_weekly_add', { text, target_date: newWeeklyDay });
    // If the chosen day is in a different week than the one on screen (e.g. adding into
    // next week), jump the grid there so the new task is actually visible.
    const targetSunday = getSunday(newWeeklyDay);
    if (targetSunday !== weekStart) setWeekStart(targetSunday);
    else loadWeekly();
  };

  const toggleWeekly = async (id, completed) => {
    // Optimistic update
    setWeeklyTasks(prev => prev.map(t => t.id === id ? { ...t, completed: !completed } : t));
    try {
      await neonQuery('greg_weekly_toggle', { id, completed: !completed });
      loadWeekly();
    } catch (e) {
      console.error('Weekly toggle error:', e);
      loadWeekly(); // revert on error
    }
  };

  const moveWeekly = async (id, target_date) => {
    await neonQuery('greg_weekly_move', { id, target_date });
    loadWeekly();
  };

  const removeWeekly = async (id) => {
    await neonQuery('greg_weekly_remove', { id });
    loadWeekly();
  };

  const reorderWeekly = async (taskId, direction, combinedList) => {
    const idx = combinedList.findIndex(item => item.kind === 'task' && item.task.id === taskId);
    if (idx < 0) return;
    const targetIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= combinedList.length) return;

    // Calculate new sort_order to place between neighbors
    const above = targetIdx > 0 && direction === 'up' ? combinedList[targetIdx - 1] : (direction === 'down' ? combinedList[targetIdx] : null);
    const below = direction === 'up' ? combinedList[targetIdx] : (targetIdx + 1 < combinedList.length ? combinedList[targetIdx + 1] : null);

    const abovePos = above ? above.sortPos : -1;
    const belowPos = below ? below.sortPos : (above ? above.sortPos + 200 : 2400);

    const newSort = Math.round((abovePos + belowPos) / 2);

    await neonQuery('greg_weekly_set_sort', { id: taskId, sort_order: newSort });
    loadWeekly();
  };

  // ── Backlog actions ──
  const addBacklog = async () => {
    const text = newBacklog.trim();
    if (!text) return;
    setNewBacklog('');
    const rows = await neonQuery('greg_backlog_add', { text });
    setBacklog(rows);
  };

  const removeBacklog = async (id) => {
    const rows = await neonQuery('greg_backlog_remove', { id });
    setBacklog(rows);
  };

  const reorderBacklog = async (id, direction) => {
    const rows = await neonQuery('greg_backlog_reorder', { id, direction });
    setBacklog(rows);
  };

  const backlogToWeekly = async (id, target_date) => {
    await neonQuery('greg_backlog_to_weekly', { id, target_date });
    loadBacklog();
    loadWeekly();
  };

  const backlogToProject = async (id, project_id) => {
    await neonQuery('greg_backlog_to_project', { id, project_id });
    loadBacklog();
    loadProjects();
  };

  // ── Computed ──
  const dailyDone = dailyItems.filter(i => i.completed).length;
  const dailyTotal = dailyItems.length;
  const dailyPct = dailyTotal > 0 ? Math.round(100 * dailyDone / dailyTotal) : 0;

  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart + 'T12:00:00');
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  // Forward-looking schedule targets for the "Schedule →" pickers (Backlog + Projects):
  // start at TODAY and run two weeks out. Never offer a past day — you can't schedule
  // into yesterday. (The This Week grid still renders all 7 day columns via weekDays.)
  const scheduleDays = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today + 'T12:00:00');
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  // ── Catch-up: past items that need triage ──
  const [showCatchUp, setShowCatchUp] = useState(false);
  const [catchUpChecked, setCatchUpChecked] = useState(false);

  const pastUnfinishedTasks = weeklyTasks.filter(t => t.target_date?.slice(0, 10) < today && !t.completed);
  const catchUpMoveDays = Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today + 'T12:00:00');
    d.setDate(d.getDate() + i + 1); // start tomorrow — "Today" is already a dedicated button
    return d.toISOString().slice(0, 10);
  });
  const pastUndismissedEvents = calEvents.filter(evt => getEventDate(evt.startTime) < today && !dismissedEvents.includes(evt.id) && !HIDDEN_EVENTS.includes(evt.subject?.toLowerCase()));
  const hasPastItems = pastUnfinishedTasks.length > 0 || pastUndismissedEvents.length > 0;

  // Auto-show catch-up only when there are past meetings to triage. Past unfinished
  // tasks live in the always-visible Carryover column, so they don't need a modal.
  useEffect(() => {
    if (activeTab === 'weekly' && pastUndismissedEvents.length > 0 && !catchUpChecked && !weeklyLoading && !calLoading) {
      setShowCatchUp(true);
      setCatchUpChecked(true);
    }
  }, [activeTab, pastUndismissedEvents.length, catchUpChecked, weeklyLoading, calLoading]);

  // Esc closes the catch-up window without touching any item.
  useEffect(() => {
    if (!showCatchUp) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShowCatchUp(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showCatchUp]);

  const dismissAllPastEvents = async () => {
    for (const evt of pastUndismissedEvents) {
      await dismissEvent(evt.id);
    }
  };

  const moveTaskToDay = async (id, targetDate) => {
    await neonQuery('greg_weekly_move', { id, target_date: targetDate });
    loadWeekly();
  };

  const weeklyToBacklog = async (id) => {
    await neonQuery('greg_weekly_to_backlog', { id });
    loadWeekly();
    loadBacklog();
  };

  const weeklyToProject = async (id, project_id) => {
    await neonQuery('greg_weekly_to_project', { id, project_id });
    loadWeekly();
    loadProjects();
  };

  const moveAllCarryoverToBacklog = async () => {
    if (pastUnfinishedTasks.length === 0) return;
    if (!window.confirm(`Move all ${pastUnfinishedTasks.length} carryover task(s) to Backlog?`)) return;
    for (const task of pastUnfinishedTasks) {
      await neonQuery('greg_weekly_to_backlog', { id: task.id });
    }
    loadWeekly();
    loadBacklog();
  };

  // ── Today tab computed ──
  // Due today OR overdue. Dates are real again now the rollover no longer flattens them, so
  // an overdue task has to be pulled in here explicitly - it is more urgent than a task due
  // today, not less, and dropping off the Today tab is exactly how a slipping item goes quiet.
  // (The weekly grid's Carryover column renders these too; it is a different tab, so no dupe.)
  const todayDated = weeklyTasks.filter(t => {
    const d = t.target_date?.slice(0, 10);
    return d && (d === today || (d < today && !t.completed));
  });
  const todayWeeklyTasks = todayDated.filter(isMineToDo);
  // Still on today, but owned by someone else. Shown separately so he can see what he
  // is waiting on without it counting as his to do.
  const todayWaitingOn = todayDated.filter(t => !isMineToDo(t) && !t.completed);

  // Pull every note for the tasks on screen in a single request - a per-tile fetch
  // would be ~40 round trips for a few hundred bytes of text.
  useEffect(() => {
    const ids = weeklyTasks.filter(t => !t.completed).map(t => String(t.id));
    if (!ids.length) return;
    let live = true;
    neonQuery('work_goals_bulk', { srcTable: 'greg_weekly_tasks', ids })
      .then(r => { if (live && r && r.goals) setNotes(r.goals); })
      .catch(() => {});
    return () => { live = false; };
  }, [weeklyTasks]);

  const saveNote = async (taskId, text) => {
    const key = String(taskId);
    setNotes(prev => ({ ...prev, [key]: text }));
    try {
      await neonQuery('work_goal_set', { srcTable: 'greg_weekly_tasks', srcId: key, goal: text });
    } catch (_) { /* the note is local either way; a failed save is not worth a modal */ }
  };
  // Seed the overlay from whatever note we already pulled in the bulk fetch, so opening
  // a tile is instant rather than a round trip.
  const openDetail = (task) => {
    setDetailTask(task);
    setDetailDraft(notes[String(task.id)] || '');
  };
  const closeDetail = async (save) => {
    if (save && detailTask && detailDraft !== (notes[String(detailTask.id)] || '')) {
      await saveNote(detailTask.id, detailDraft);
    }
    setDetailTask(null);
    setDetailDraft('');
  };

  const todayEvents = calEvents.filter(evt => getEventDate(evt.startTime) === today && !HIDDEN_EVENTS.includes(evt.subject?.toLowerCase()));
  const todayCombined = [
    ...todayEvents.map(evt => ({ kind: 'event', event: evt, sortPos: evt.isAllDay ? -1 : getEventMinutes(evt.startTime), id: 'evt-' + evt.id })),
    ...todayWeeklyTasks.map(t => ({ kind: 'task', task: t, sortPos: t.sort_order ?? 2400, id: 'task-' + t.id })),
  ].sort((a, b) => a.sortPos - b.sortPos);
  const todayAllItems = [...dailyItems, ...todayWeeklyTasks, ...todayEvents];
  const todayDoneCount = dailyItems.filter(i => i.completed).length + todayWeeklyTasks.filter(t => t.completed).length + todayEvents.filter(e => dismissedEvents.includes(e.id)).length;
  const todayTotalCount = dailyItems.length + todayWeeklyTasks.length + todayEvents.length;
  const todayPct = todayTotalCount > 0 ? Math.round(100 * (dailyItems.filter(i => i.completed).length + todayWeeklyTasks.filter(t => t.completed).length) / (dailyItems.length + todayWeeklyTasks.length || 1)) : 0;

  const tabs = [
    { key: 'today', label: 'Tasks', count: todayPct < 100 ? `${todayPct}%` : null, color: todayPct === 100 ? 'var(--green)' : 'var(--orange)' },
    { key: 'daily', label: 'Daily', count: `${dailyDone}/${dailyTotal}` },
    { key: 'weekly', label: 'This Week' },
    { key: 'projects', label: 'Projects', count: projectsData.projects.length || null },
    { key: 'backlog', label: 'Backlog', count: backlog.length },
  ];

  return (
    <div className="todo-page" style={{ padding: 24, maxWidth: activeTab === 'today' ? 1600 : (activeTab === 'weekly' ? 1280 : activeTab === 'projects' ? 1500 : 1100) }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <User size={24} color="var(--navy)" />
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Greg</h1>
        <QuickCapture onSaved={loadInbox} />
        <button onClick={() => navigateTo && navigateTo('team-board')} title="The team board"
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 12px", background: "var(--navy)", color: "#fff", border: "1px solid var(--navy)", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
          <Users size={13} /> Team
        </button>
        {inbox.length > 0 && (
          <button onClick={() => { setActiveTab("today"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: "#fff3ed", color: "var(--orange)", border: "1px solid var(--orange)", borderRadius: 20, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            <Inbox size={13} /> {inbox.length} to place
          </button>
        )}
        {activeTab === 'today' && (dailyItems.length + todayWeeklyTasks.length) > 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: todayPct === 100 ? 'var(--green)' : 'var(--orange)', background: todayPct === 100 ? '#e6f4ea' : '#fff3ed', padding: '2px 10px', borderRadius: 10 }}>
            {todayPct}%
          </span>
        )}
        {activeTab === 'daily' && dailyTotal > 0 && (
          <span style={{ fontSize: 12, fontWeight: 700, color: dailyPct === 100 ? 'var(--green)' : 'var(--orange)', background: dailyPct === 100 ? '#e6f4ea' : '#fff3ed', padding: '2px 10px', borderRadius: 10 }}>
            {dailyPct}%
          </span>
        )}
      </div>

      {/* ── The frog: the one thing, above everything, on every tab ── */}
      {/* Above the tab bar on purpose. The paralysis this replaces was not caused by any one
          list being wrong, it was caused by having to pick a list before picking a task. */}
      {/* The strip is rendered BY the card, not beside it: both read "which habits are already
          scheduled" from the same response, and two components polling separately would drift
          on that the moment one of them was a render behind. */}
      {/* Today's board replaces the single frog card once a day has been set (Greg, 2026-09-24).
          On a morning with nothing set, the ranked frog still shows as the suggestion. */}
      {/* One frog a day, planned ahead (Greg, 2026-11-03). Today's frog also leads the board below. */}
      <FrogWeek refreshKey={frogRefresh + boardRefresh} onChanged={() => { setBoardRefresh(n => n + 1); loadWeekly(); }} />
      <TodayBoard
        refreshKey={boardRefresh}
        stepCount={stepCount}
        dailyItems={dailyItems}
        onKeys={setBoardKeys}
        onChanged={() => { loadWeekly(); loadBacklog(); loadProjects(); loadDaily(); setFrogRefresh(n => n + 1); }}
        fallback={
          <FrogCard
            onPlan={() => navigateTo && navigateTo('todo-plan')}
            dailyItems={activeTab === 'daily' ? [] : dailyItems}
            onChanged={() => { loadWeekly(); loadBacklog(); loadProjects(); loadDaily(); }} />
        } />

      {/* ── Waiting on someone else (left) and things to place (right), side by side ── */}
      {(todayWaitingOn.length > 0 || inbox.length > 0) && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 14, alignItems: 'start', marginBottom: 16 }}>
        {todayWaitingOn.length > 0 && (
          <div style={{ border: '1px solid #d0daf0', background: '#f7f9ff', borderRadius: 8, padding: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>
              Waiting on someone else
            </div>
            {todayWaitingOn.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12, color: '#5a6b7a', marginBottom: 3 }}>
                <span style={{ fontWeight: 700, color: '#4a5568', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{String(t.owner || '').replace(/-/g, ' ')}</span>
                <span>{t.text}</span>
              </div>
            ))}
            <div style={{ fontSize: 10.5, color: '#8b94a3', marginTop: 4 }}>
              Off your list. It comes back in the morning brief if they go quiet.
            </div>
          </div>
        )}
        {/* ── Inbox: captures waiting to be placed ── */}
        {inbox.length > 0 && (
          <div style={{ border: "1px solid var(--orange)", background: "#fffaf6", borderRadius: 8, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <Inbox size={16} color="var(--orange)" />
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
                {inbox.length === 1 ? "One thing to place" : inbox.length + " things to place"}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--gray-500)" }}>
                Nobody sees these until you put them somewhere
              </div>
            </div>
            {inbox.map((it) => (
              <div key={it.id} style={{ borderTop: "1px solid #f0dfd3", padding: "10px 0", opacity: placing === it.id ? 0.5 : 1 }}>
                <div style={{ fontSize: 13.5, color: "#22303c", marginBottom: 8, lineHeight: 1.5 }}>{it.text}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                  <button onClick={() => place(it.id, "today")} disabled={placing === it.id} style={placeBtn(true)}>Today</button>
                  <button onClick={() => place(it.id, "week")} disabled={placing === it.id} style={placeBtn()}>This week</button>
                  <button onClick={() => place(it.id, "month")} disabled={placing === it.id} style={placeBtn()}>Later</button>
                  <button onClick={() => place(it.id, "someday")} disabled={placing === it.id} style={placeBtn()}>Someday</button>
                  <select defaultValue="" disabled={placing === it.id}
                    onChange={(e) => { if (e.target.value) place(it.id, "week", e.target.value); }}
                    style={{ padding: "5px 8px", border: "1px solid var(--gray-300)", borderRadius: 14, fontSize: 12, fontFamily: "inherit", background: "#fff", cursor: "pointer" }}>
                    <option value="">Give to...</option>
                    {owners.map((o) => <option key={o.owner_key} value={o.owner_key}>{o.display_name}</option>)}
                  </select>
                  <button onClick={() => binCapture(it.id)} disabled={placing === it.id} title="Not a task"
                    style={{ ...placeBtn(), marginLeft: "auto", color: "var(--gray-500)", display: "flex", alignItems: "center", gap: 4 }}>
                    <Trash2 size={12} /> Bin
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Tabs */}
      <div className="todo-tabs" style={{ display: 'flex', gap: 0, marginBottom: 0, borderBottom: '2px solid var(--gray-200)' }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.key;
          const isToday = tab.key === 'today';
          const todayActive = isToday && isActive;
          const todayBadgeBg = todayPct === 100 ? '#16a34a' : todayPct >= 50 ? 'var(--orange)' : '#dc2626';
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              display: 'flex', alignItems: 'center', gap: isToday ? 8 : 6,
              padding: isToday ? '10px 24px' : '10px 20px', border: 'none',
              borderBottom: todayActive ? '3px solid var(--orange)' : isActive ? '3px solid var(--navy)' : '3px solid transparent',
              background: todayActive ? 'var(--navy)' : isActive ? '#e8f0fe' : 'transparent',
              color: todayActive ? '#fff' : isActive ? 'var(--navy)' : 'var(--gray-700)',
              fontWeight: isToday ? 800 : isActive ? 700 : 500,
              fontSize: isToday ? 14 : 13.5, cursor: 'pointer', fontFamily: 'inherit',
              marginBottom: -2, borderRadius: '6px 6px 0 0',
              letterSpacing: isToday ? '0.5px' : undefined,
              textTransform: isToday ? 'uppercase' : undefined,
              boxShadow: todayActive ? '0 -2px 8px rgba(220, 88, 42, 0.15)' : undefined,
              transition: 'all 0.15s ease',
            }}>
              {tab.label}
              {tab.count != null && (
                <span style={{
                  background: isToday ? todayBadgeBg : 'var(--navy)',
                  color: '#fff', fontSize: isToday ? 11 : 10, fontWeight: 700, borderRadius: 10,
                  padding: isToday ? '2px 9px' : '1px 7px',
                  boxShadow: isToday ? `0 0 6px ${todayBadgeBg}40` : undefined,
                }}>{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div style={{ background: 'var(--white)', border: '1px solid var(--gray-200)', borderTop: 'none', borderRadius: '0 0 8px 8px', minHeight: 300, padding: 20 }}>

        {/* ═══════ TODAY ═══════ */}
        {activeTab === 'today' && (() => {
          // Build all today's items as a unified pool
          // Habits are NOT in this pool any more - they render as the DailyStrip above. While
          // they were in it, twenty-three of them drowned the real work: "23 things left today"
          // was mostly hydration, and the tasks he opened the page for started below the fold.
          const allItems = [
            ...todayWeeklyTasks.filter(t => !onBoard('greg_weekly_tasks', t.id)).map(t => ({ key: `task-${t.id}`, label: t.text, type: 'task', done: !!t.completed, data: t })),
            ...todayEvents.filter(e => !onBoard('calendar', e.id)).map(e => ({
              key: `event-${e.id}`, label: e.subject, type: 'event', done: dismissedEvents.includes(e.id), data: e,
              time: e.isAllDay ? 'All day' : `${formatEventTime(e.startTime)} – ${formatEventTime(e.endTime)}`,
              location: e.location,
            })),
          ];
          const poolItems = allItems.filter(i => !i.done);
          const doneItems = allItems.filter(i => i.done);

          const toggleFrog = async (itemKey) => {
            const id = String(itemKey).replace(/^task-/, '');
            if (frogKey === itemKey) {
              setFrogKey(null);
              await boardCall('frog_clear', { date: today }).catch(() => {});
            } else {
              setFrogKey(itemKey);
              await boardCall('frog_set', { date: today, srcTable: 'greg_weekly_tasks', srcId: id }).catch(() => {});
            }
            setBoardRefresh(n => n + 1);
          };

          const handleComplete = (item) => {
            // 10k steps is auto-only — can't manually toggle
            if (item.type === 'daily' && item.label?.toLowerCase().includes('10k steps')) return;
            if (item.type === 'daily') toggleDaily(item.data.id, item.data.completed);
            else if (item.type === 'task') toggleWeekly(item.data.id, item.data.completed);
            else if (item.type === 'event') dismissEvent(item.data.id);
            // Remove from queue after completing
            neonQuery('today_queue_remove', { date: today, item_key: item.key })
              .then(rows => setQueue(rows)).catch(() => {});
          };

          // Ticked something by accident? Put it straight back on the board.
          const handleUndo = (item) => {
            if (item.type === 'daily') toggleDaily(item.data.id, true);
            else if (item.type === 'task') toggleWeekly(item.data.id, true);
            else if (item.type === 'event') undismissEvent(item.data.id);
          };

          // ⚠️ TAPPING A TASK PUTS IT ON "EVERYTHING FOR TODAY". It used to drop into an Up Next
          // queue here, which Greg (2026-09-30) called out as a duplicate of Upcoming on the board:
          // "what I really wanted to do was put it in everything for today." Up Next is gone;
          // the board is the only place a day gets built.
          const putOnToday = async (item) => {
            if (item.type !== 'task') return;
            try {
              await boardCall('place', { srcTable: 'greg_weekly_tasks', srcId: String(item.data.id) });
              setBoardRefresh(n => n + 1);
            } catch (e) { console.error(e); }
          };

          const TILE = (item, opts = {}) => {
            const isEvent = item.type === 'event';
            const isFrog = frogKey === item.key;
            const canFrog = item.type === 'task' && !item.done; // frogs only for weekly tasks
            const noteId = item.type === 'task' && item.data ? String(item.data.id) : null;
            const noteText = noteId ? (notes[noteId] || '') : '';
            const noteShown = noteOpen === item.key;
            return (
              <div
                key={item.key}
                className={opts.inQueue ? undefined : 'todo-tile-pool'}
                draggable={!item.done}
                onDragStart={e => {
                  // Same payload the board's own list uses, so a task can be dragged straight into Upcoming.
                  e.dataTransfer.setData('text/plain', item.type === 'task'
                    ? JSON.stringify({ kind: 'task', srcTable: 'greg_weekly_tasks', srcId: String(item.data.id) }) : item.key);
                  setDragItem(item.key);
                }}
                onDragEnd={() => setDragItem(null)}
                onClick={item.done ? undefined : opts.inQueue ? () => handleComplete(item) : () => putOnToday(item)}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 0, padding: '10px 14px',
                  background: item.done ? '#e6f4ea' : isFrog ? '#f0fff0' : isEvent ? '#f0f4ff' : item.type === 'task' ? '#fff6f0' : '#fff',
                  borderRadius: 8,
                  border: item.done ? '1px solid #b7e1c4' : isFrog ? '2px solid #38a169' : isEvent ? '1px solid #d0daf0' : item.type === 'task' ? '1px solid #fcd5c0' : '1px solid var(--gray-200)',
                  cursor: item.done ? 'default' : opts.inQueue ? 'pointer' : 'grab',
                  width: opts.inQueue ? 'auto' : 'calc(33.333% - 4px)', minWidth: opts.inQueue ? 'auto' : 200, boxSizing: 'border-box',
                  opacity: dragItem === item.key ? 0.4 : item.done ? 0.7 : 1,
                  transition: 'all 0.15s', fontSize: 12.5,
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                {isFrog && <span style={{ fontSize: 16, flexShrink: 0, lineHeight: 1 }}>🐸</span>}
                {isEvent && !isFrog && <Calendar size={14} color="#0078d4" style={{ flexShrink: 0 }} />}
                {opts.inQueue && !isEvent && (
                  <div style={{
                    width: 20, height: 20, borderRadius: 5,
                    border: isFrog ? '2px solid #38a169' : item.label?.toLowerCase().includes('10k steps') ? '2px dashed var(--gray-300)' : '2px solid var(--gray-300)',
                    background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, lineHeight: 1.3 }}>
                  <div style={{ fontWeight: isFrog ? 700 : 500, color: item.done ? 'var(--gray-500)' : isFrog ? '#276749' : isEvent ? '#2d3748' : 'var(--gray-900)', textDecoration: item.done ? 'line-through' : 'none' }}>
                    {item.label}
                  </div>
                  {isEvent && item.time && (
                    <div style={{ fontSize: 10, color: '#718096', marginTop: 1 }}>
                      {item.time}
                      {(() => {
                        const loc = splitLocation(item.location);
                        if (!loc) return null;
                        return (<>
                          {loc.text ? ` · ${loc.text}` : ''}
                          {loc.url && (<>
                            {' · '}
                            <a href={loc.url} target="_blank" rel="noreferrer" title={loc.url}
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: '#0078d4', fontWeight: 600, textDecoration: 'none' }}>Join</a>
                          </>)}
                        </>);
                      })()}
                    </div>
                  )}
                  {isFrog && <div style={{ fontSize: 9, color: '#38a169', fontWeight: 600, marginTop: 1 }}>EAT THIS FROG</div>}
                  {item.label?.toLowerCase().includes('10k steps') && stepCount !== null && !item.done && (
                    <div style={{ marginTop: 3 }}>
                      <div style={{ fontSize: 9, color: stepCount >= 10000 ? 'var(--green)' : 'var(--gray-500)', fontWeight: 600 }}>
                        {Number(stepCount).toLocaleString()} / 10,000
                      </div>
                      <div style={{ height: 4, background: 'var(--gray-200)', borderRadius: 2, overflow: 'hidden', marginTop: 2 }}>
                        <div style={{ height: '100%', width: `${Math.min(100, (stepCount / 10000) * 100)}%`, background: stepCount >= 10000 ? 'var(--green)' : stepCount >= 7500 ? '#f59e0b' : 'var(--orange)', borderRadius: 2, transition: 'width 0.3s' }} />
                      </div>
                    </div>
                  )}
                </div>
                {canFrog && (
                  <button
                    onClick={e => { e.stopPropagation(); toggleFrog(item.key); }}
                    title={isFrog ? 'Remove frog' : 'Set as your frog — most important task'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                      fontSize: 14, lineHeight: 1, flexShrink: 0,
                      opacity: isFrog ? 1 : 0.3, transition: 'opacity 0.15s',
                    }}
                  >
                    🐸
                  </button>
                )}
                {opts.inQueue && !canFrog && (
                  <div style={{ fontSize: 10, color: 'var(--gray-400)', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                    <Check size={12} />
                  </div>
                )}
                {item.done && (
                  <button
                    onClick={e => { e.stopPropagation(); handleUndo(item); }}
                    title="Put this back — it is not done after all"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0,
                      background: '#fff', border: '1px solid #b7e1c4', borderRadius: 5,
                      padding: '2px 7px', fontSize: 10, fontWeight: 600, fontFamily: 'inherit',
                      color: 'var(--gray-600)', cursor: 'pointer',
                    }}
                  >
                    <RotateCcw size={10} /> Undo
                  </button>
                )}
                {!opts.inQueue && !item.done && !canFrog && (
                  <ArrowRight size={12} color="var(--gray-400)" style={{ flexShrink: 0 }} />
                )}
                {!opts.inQueue && !item.done && item.type === 'task' && (
                  <select
                    value=""
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    onChange={e => {
                      e.stopPropagation();
                      const v = e.target.value;
                      if (v === 'backlog') weeklyToBacklog(item.data.id);
                      else if (v.startsWith('proj-')) weeklyToProject(item.data.id, parseInt(v.slice(5)));
                      else if (v.startsWith('day-')) moveWeekly(item.data.id, v.slice(4));
                    }}
                    title="Move this to another day, or off today into Backlog / a Project"
                    style={{ fontSize: 10, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)', flexShrink: 0, maxWidth: 108, background: '#fff' }}
                  >
                    <option value="" disabled>Move →</option>
                    <optgroup label="To another day">
                      {scheduleDays.slice(1).map(d => <option key={d} value={`day-${d}`}>{formatDay(d)}</option>)}
                    </optgroup>
                    <optgroup label="Off today">
                      <option value="backlog">📥 Backlog</option>
                      {projectsData.projects.map(p => <option key={p.id} value={`proj-${p.id}`}>📁 {p.name}</option>)}
                    </optgroup>
                  </select>
                )}
                {noteId && (
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      if (noteShown) { setNoteOpen(null); return; }
                      setNoteDraft(noteText);
                      setNoteOpen(item.key);
                    }}
                    title={noteText ? 'Show the note' : 'Add a note'}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer', padding: 2,
                      lineHeight: 1, flexShrink: 0, display: 'flex',
                      color: noteText ? 'var(--orange)' : 'var(--gray-300)',
                    }}
                  >
                    <FileText size={13} />
                  </button>
                )}
                </div>

                {noteId && noteShown && (
                  <div
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                    draggable={false}
                    onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                    style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(0,0,0,0.07)' }}
                  >
                    <textarea
                      value={noteDraft}
                      autoFocus
                      onChange={e => setNoteDraft(e.target.value)}
                      onBlur={() => { if (noteDraft !== noteText) saveNote(item.data.id, noteDraft); }}
                      placeholder="What do you actually need to say or remember here?"
                      rows={noteDraft.length > 260 ? 8 : noteDraft.length > 110 ? 5 : 3}
                      style={{
                        width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', fontSize: 12,
                        lineHeight: 1.5, color: 'var(--gray-800)', background: 'rgba(255,255,255,0.75)',
                        border: '1px solid var(--gray-300)', borderRadius: 6, padding: '6px 8px',
                        resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 5, alignItems: 'center' }}>
                      <button
                        onClick={() => { saveNote(item.data.id, noteDraft); setNoteOpen(null); }}
                        style={{ fontSize: 10, padding: '3px 9px', borderRadius: 4, border: 'none', cursor: 'pointer', background: 'var(--orange)', color: '#fff', fontWeight: 600 }}
                      >Save</button>
                      <button
                        onClick={() => setNoteOpen(null)}
                        style={{ fontSize: 10, padding: '3px 9px', borderRadius: 4, border: '1px solid var(--gray-300)', cursor: 'pointer', background: '#fff', color: 'var(--gray-600)' }}
                      >Close</button>
                      {noteText && (
                        <button
                          onClick={() => { saveNote(item.data.id, ''); setNoteDraft(''); setNoteOpen(null); }}
                          style={{ fontSize: 10, padding: '3px 9px', borderRadius: 4, border: 'none', cursor: 'pointer', background: 'none', color: 'var(--gray-400)', marginLeft: 'auto' }}
                        >Clear</button>
                      )}
                    </div>
                  </div>
                )}

                {noteId && !noteShown && noteText && (
                  <div
                    onClick={e => { e.stopPropagation(); setNoteDraft(noteText); setNoteOpen(item.key); }}
                    title="Click to edit"
                    style={{
                      marginTop: 6, fontSize: 11, lineHeight: 1.45, color: 'var(--gray-600)',
                      cursor: 'text', display: '-webkit-box', WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}
                  >
                    {noteText}
                  </div>
                )}
              </div>
            );
          };

          return (
            <div className="todo-today-layout" style={{ display: 'flex', gap: 20, minHeight: 400 }}>
              {/* LEFT: Pool */}
              <div
                className="todo-today-pool"
                style={{ flex: 1, minWidth: 0 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                    {new Date(today + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>
                    Tap one to put it on Everything for today, or drag it into Upcoming
                  </div>
                </div>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const input = e.target.elements.quickTask;
                  const text = input.value.trim();
                  if (!text) return;
                  input.value = '';
                  await neonQuery('greg_weekly_add', { text, target_date: today });
                  loadWeekly();
                }} style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <input name="quickTask" placeholder="Quick add a task for today..." autoComplete="off"
                    style={{ flex: 1, padding: '7px 12px', border: '1px solid var(--gray-300)', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', outline: 'none' }} />
                  <button type="submit" style={{ padding: '7px 14px', background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Plus size={14} /> Add
                  </button>
                </form>

                {/* ── Tasks (Weekly) ── */}
                {(() => {
                  const taskAll = allItems.filter(i => i.type === 'task');
                  const taskPool = poolItems.filter(i => i.type === 'task');
                  const taskDoneCount = taskAll.filter(i => i.done).length;
                  const allDone = taskAll.length > 0 && taskDoneCount === taskAll.length;
                  if (taskAll.length === 0) return null;
                  // Sweep everything you did NOT queue for today back into the Backlog in one shot,
                  // so you're left looking only at the handful you committed to. Resort tomorrow.
                  const sweepToBacklog = async () => {
                    if (taskPool.length === 0) return;
                    if (!window.confirm(`Move ${taskPool.length} task(s) to Backlog?

Anything already on your board stays there.`)) return;
                    for (const it of taskPool) { await neonQuery('greg_weekly_to_backlog', { id: it.data.id }); }
                    loadWeekly(); loadBacklog();
                  };
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--navy)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                        Tasks
                        <span style={{ fontSize: 9, background: allDone ? 'var(--green)' : 'var(--navy)', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>
                          {taskDoneCount}/{taskAll.length}{allDone ? ' — Done' : ''}
                        </span>
                        {taskPool.length > 1 && (
                          <button onClick={sweepToBacklog} title={`Move the ${taskPool.length} task(s) here that are not on your board into the Backlog`}
                            style={{ display: 'flex', alignItems: 'center', gap: 3, marginLeft: 'auto', padding: '2px 8px', fontSize: 9.5, fontWeight: 700, color: '#b45309', background: '#fff7ed', border: '1px solid #f59e0b', borderRadius: 5, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'none', letterSpacing: 0 }}>
                            <Archive size={11} /> Sweep {taskPool.length} → Backlog
                          </button>
                        )}
                      </div>
                      {taskPool.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {taskPool.map(item => TILE(item))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Meetings (Calendar) ── */}
                {(() => {
                  const eventAll = allItems.filter(i => i.type === 'event');
                  const eventPool = poolItems.filter(i => i.type === 'event');
                  const eventDoneCount = eventAll.filter(i => i.done).length;
                  const allDone = eventAll.length > 0 && eventDoneCount === eventAll.length;
                  if (eventAll.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: allDone ? 'var(--green)' : '#0078d4', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Meetings
                        <span style={{ fontSize: 9, background: allDone ? 'var(--green)' : '#0078d4', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>
                          {eventDoneCount}/{eventAll.length}{allDone ? ' — Done for the day' : ''}
                        </span>
                      </div>
                      {eventPool.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {eventPool.map(item => TILE(item))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Daily To-Do ── */}
                {(() => {
                  const dailyAll = allItems.filter(i => i.type === 'daily');
                  const dailyPool = poolItems.filter(i => i.type === 'daily');
                  const dailyDoneCount = dailyAll.filter(i => i.done).length;
                  const allDone = dailyAll.length > 0 && dailyDoneCount === dailyAll.length;
                  if (dailyAll.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: allDone ? 'var(--green)' : 'var(--orange)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                        Daily To-Do
                        <span style={{ fontSize: 9, background: allDone ? 'var(--green)' : 'var(--orange)', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>
                          {dailyDoneCount}/{dailyAll.length}{allDone ? ' — Done' : ''}
                        </span>
                      </div>
                      {dailyPool.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {dailyPool.map(item => TILE(item))}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {poolItems.length === 0 && doneItems.length === 0 && (
                  <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-400)', fontSize: 12 }}>
                    No items today
                  </div>
                )}

                {/* ── Completed ── */}
                {doneItems.length > 0 && (
                  <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--green)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                      Completed
                      <span style={{ fontSize: 9, background: 'var(--green)', color: '#fff', borderRadius: 8, padding: '1px 6px', fontWeight: 700 }}>{doneItems.length}</span>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {doneItems.map(item => TILE(item))}
                    </div>
                  </div>
                )}
              </div>

            </div>
          );
        })()}

        {/* ═══════ DAILY ═══════ */}
        {activeTab === 'daily' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={newDaily} onChange={e => setNewDaily(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addDailyTemplate()}
                placeholder="Add a recurring daily task..." style={INPUT_STYLE} />
              <button onClick={addDailyTemplate} disabled={!newDaily.trim()} style={BTN_STYLE('var(--navy)', !newDaily.trim())}>
                <Plus size={14} /> Add
              </button>
              <button onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
                style={{ ...BTN_STYLE('var(--gray-600)', false), background: showHistory ? 'var(--navy)' : 'var(--gray-200)', color: showHistory ? '#fff' : 'var(--gray-700)' }}>
                <BarChart2 size={14} /> History
              </button>
            </div>

            {dailyLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading...</div>
            ) : dailyItems.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5 }}>No daily tasks set up yet. Add your recurring tasks above.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {dailyItems.map((item, idx) => (
                  <div key={item.id}
                    draggable
                    onDragStart={() => setDailyDragIdx(idx)}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); handleDailyDrop(idx); }}
                    onDragEnd={() => setDailyDragIdx(null)}
                    style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: dailyDragIdx === idx ? '#e8f0fe' : item.completed ? '#e6f4ea' : 'var(--gray-50)', borderRadius: 6,
                    border: dailyDragIdx === idx ? '2px dashed var(--navy)' : item.completed ? '1px solid #b7e1c4' : '1px solid var(--gray-200)',
                    transition: 'all 0.15s', cursor: 'grab',
                    opacity: dailyDragIdx !== null && dailyDragIdx !== idx ? 0.7 : 1,
                  }}>
                    <GripVertical size={14} color="var(--gray-300)" style={{ flexShrink: 0, cursor: 'grab' }} />
                    {(() => {
                      const isAutoOnly = item.text?.toLowerCase().includes('10k steps');
                      return (
                        <button onClick={() => !isAutoOnly && toggleDaily(item.id, item.completed)} title={isAutoOnly ? 'Auto-completes from Apple Health' : undefined} style={{
                          width: 20, height: 20, borderRadius: 4, border: item.completed ? 'none' : isAutoOnly ? '2px dashed var(--gray-300)' : '2px solid var(--gray-400)',
                          background: item.completed ? 'var(--green)' : 'transparent', cursor: isAutoOnly ? 'default' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          {item.completed && <Check size={12} color="#fff" strokeWidth={3} />}
                        </button>
                      );
                    })()}
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 12.5, color: item.completed ? 'var(--gray-500)' : 'var(--gray-900)', textDecoration: item.completed ? 'line-through' : 'none' }}>{item.text}</span>
                      {item.text?.toLowerCase().includes('10k steps') && stepCount !== null && (
                        <div style={{ marginTop: 4 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: stepCount >= 10000 ? 'var(--green)' : 'var(--gray-500)', fontWeight: 600, marginBottom: 2 }}>
                            <span>{stepCount >= 10000 ? 'Goal reached!' : `${Number(stepCount).toLocaleString()} / 10,000`}</span>
                            <span>{Math.min(100, Math.round((stepCount / 10000) * 100))}%</span>
                          </div>
                          <div style={{ height: 5, background: 'var(--gray-200)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(100, (stepCount / 10000) * 100)}%`, background: stepCount >= 10000 ? 'var(--green)' : stepCount >= 7500 ? '#f59e0b' : 'var(--orange)', borderRadius: 3, transition: 'width 0.3s' }} />
                          </div>
                        </div>
                      )}
                    </div>
                    <button onClick={() => removeDailyTemplate(item.id)} title="Remove from daily list" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 2 }}>
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* History */}
            {showHistory && history.length > 0 && (
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 8 }}>Completion History (last 30 days)</div>
                <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 60 }}>
                  {[...history].reverse().map(h => {
                    const pct = Number(h.pct ?? 0);
                    return (
                      <div key={h.log_date} title={`${new Date(h.log_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}: ${h.done}/${h.total} (${pct}%)`}
                        style={{ flex: 1, minWidth: 8, maxWidth: 20, height: `${Math.max(4, pct)}%`, borderRadius: '3px 3px 0 0',
                          background: pct === 100 ? 'var(--green)' : pct >= 50 ? 'var(--orange)' : 'var(--red)', opacity: 0.8 }} />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═══════ WEEKLY ═══════ */}
        {activeTab === 'weekly' && (
          <div>
            {/* Catch-Up Modal */}
            {showCatchUp && hasPastItems && (
              <div onClick={() => setShowCatchUp(false)} style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <div onClick={e => e.stopPropagation()} style={{
                  background: '#fff', borderRadius: 12, padding: 24, width: 520, maxHeight: '80vh',
                  overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 4px' }}>
                    <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Daily Catch-Up</h2>
                    <button onClick={() => setShowCatchUp(false)} title="Close and leave everything where it is"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: '1px solid var(--gray-300)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', color: 'var(--gray-600)' }}>
                      <X size={13} /> Close
                    </button>
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--gray-600)', margin: '0 0 16px' }}>
                    You have items from previous days that need attention.
                  </p>

                  {/* Past calendar events */}
                  {pastUndismissedEvents.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)' }}>
                          Past Meetings ({pastUndismissedEvents.length})
                        </span>
                        <button onClick={dismissAllPastEvents} style={{
                          fontSize: 11, fontWeight: 600, color: '#fff', background: 'var(--green)',
                          border: 'none', borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          Clear All Meetings
                        </button>
                      </div>
                      {pastUndismissedEvents.map(evt => (
                        <div key={evt.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                          background: '#f0f4ff', borderRadius: 4, border: '1px solid #d0daf0',
                          fontSize: 11, marginBottom: 4,
                        }}>
                          <Calendar size={11} color="#0078d4" style={{ flexShrink: 0 }} />
                          <span style={{ flex: 1, color: '#2d3748' }}>{evt.subject}</span>
                          <span style={{ fontSize: 10, color: '#718096', flexShrink: 0 }}>
                            {formatDay(getEventDate(evt.startTime))}
                          </span>
                          <button onClick={async () => {
                            await neonQuery('greg_weekly_add', { text: evt.subject, target_date: today });
                            await dismissEvent(evt.id);
                            loadWeekly();
                          }} title="Convert to task for today"
                            style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Today
                          </button>
                          <select onChange={async e => {
                            if (e.target.value) {
                              await neonQuery('greg_weekly_add', { text: evt.subject, target_date: e.target.value });
                              await dismissEvent(evt.id);
                              loadWeekly();
                            }
                            e.target.value = '';
                          }} defaultValue="" style={{ fontSize: 10, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 4px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)' }}>
                            <option value="" disabled>Move to...</option>
                            {catchUpMoveDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
                          </select>
                          <button onClick={() => dismissEvent(evt.id)} title="Clear"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', padding: 2, display: 'flex' }}>
                            <Check size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Past unfinished tasks */}
                  {pastUnfinishedTasks.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--gray-600)', display: 'block', marginBottom: 8 }}>
                        Unfinished Tasks ({pastUnfinishedTasks.length})
                      </span>
                      {pastUnfinishedTasks.map(task => (
                        <div key={task.id} style={{
                          display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                          background: 'var(--gray-50)', borderRadius: 4, border: '1px solid var(--gray-200)',
                          fontSize: 11, marginBottom: 4,
                        }}>
                          <span style={{ flex: 1, color: 'var(--gray-800)' }}>{task.text}</span>
                          <span style={{ fontSize: 10, color: 'var(--gray-400)', flexShrink: 0 }}>
                            {formatDay(task.target_date?.slice(0, 10))}
                          </span>
                          <button onClick={() => { toggleWeekly(task.id, false); }} title="Mark done"
                            style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Done
                          </button>
                          <button onClick={() => moveTaskToDay(task.id, today)} title="Move to today"
                            style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                            Today
                          </button>
                          <select onChange={e => { if (e.target.value) { moveTaskToDay(task.id, e.target.value); } e.target.value = ''; }}
                            defaultValue="" style={{ fontSize: 10, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '3px 4px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)' }}>
                            <option value="" disabled>Move to...</option>
                            {catchUpMoveDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
                          </select>
                          <button onClick={() => removeWeekly(task.id)} title="Delete"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 2, display: 'flex' }}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={() => setShowCatchUp(false)} style={{
                      padding: '8px 20px', background: 'var(--navy)', color: '#fff', border: 'none',
                      borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                    }}>
                      All Caught Up
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Week nav */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <button onClick={() => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() - 7); setWeekStart(d.toISOString().slice(0, 10)); setWeeklyLoading(true); }}
                style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>← Prev</button>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>
                Week of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
              </span>
              <button onClick={() => { const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + 7); setWeekStart(d.toISOString().slice(0, 10)); setWeeklyLoading(true); }}
                style={{ background: 'none', border: '1px solid var(--gray-300)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Next →</button>
              <button onClick={() => { setWeekStart(getSunday(today)); setWeeklyLoading(true); }}
                style={{ background: 'var(--gray-200)', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 }}>Today</button>
              <div style={{ marginLeft: 'auto' }}>
                {msAccount ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Calendar size={13} color="var(--green)" />
                    <span style={{ fontSize: 11, color: 'var(--gray-600)' }}>{msAccount.username}</span>
                    <button onClick={handleMsLogout} title="Disconnect Outlook"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 2, display: 'flex' }}>
                      <LogOut size={12} />
                    </button>
                  </div>
                ) : (
                  <button onClick={handleMsLogin} style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '4px 12px',
                    background: '#0078d4', color: '#fff', border: 'none', borderRadius: 6,
                    fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                  }}>
                    <LogIn size={12} /> Connect Outlook
                  </button>
                )}
              </div>
            </div>

            {/* Add task */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={newWeekly} onChange={e => setNewWeekly(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addWeeklyTask()}
                placeholder="Add a task for this week..." style={INPUT_STYLE} />
              <select value={newWeeklyDay} onChange={e => setNewWeeklyDay(e.target.value)} style={{ ...INPUT_STYLE, flex: 'none', width: 128 }}>
                {scheduleDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
              </select>
              <button onClick={addWeeklyTask} disabled={!newWeekly.trim()} style={BTN_STYLE('var(--navy)', !newWeekly.trim())}>
                <Plus size={14} /> Add
              </button>
            </div>

            {weeklyLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'minmax(140px, 0.85fr) repeat(7, minmax(0, 1fr))', gap: 10 }}>
                {/* Carryover column — past unfinished tasks, newest first */}
                {(() => {
                  const carryover = [...pastUnfinishedTasks].sort((a, b) =>
                    (b.target_date || '').localeCompare(a.target_date || '')
                  );
                  return (
                    <div style={{ background: '#fff7ed', borderRadius: 8, border: '1px dashed #f59e0b', padding: 10, minHeight: 150, minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309', marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Carryover</span>
                        {carryover.length > 0 && <span style={{ fontSize: 10, color: '#b45309' }}>{carryover.length}</span>}
                      </div>
                      {carryover.length > 0 && (
                        <button onClick={moveAllCarryoverToBacklog} title="Move all carryover tasks to Backlog"
                          style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', justifyContent: 'center', marginBottom: 8, padding: '4px 6px', fontSize: 10, fontWeight: 600, color: '#b45309', background: '#fff', border: '1px solid #f59e0b', borderRadius: 4, cursor: 'pointer' }}>
                          <Archive size={11} /> Move all to Backlog
                        </button>
                      )}
                      {carryover.length === 0 ? (
                        <div style={{ fontSize: 10, color: 'var(--gray-400)', fontStyle: 'italic', lineHeight: 1.4 }}>
                          No leftover tasks — caught up.
                        </div>
                      ) : carryover.map(task => (
                        <div key={task.id} style={{
                          marginBottom: 5, fontSize: 11,
                          background: '#fff', border: '1px solid #fcd9a8', borderRadius: 5, padding: '4px 5px',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                            <button onClick={() => toggleWeekly(task.id, false)} title="Mark done"
                              style={{
                                width: 15, height: 15, borderRadius: 3, border: '1.5px solid var(--gray-400)',
                                background: 'transparent', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                              }} />
                            <span style={{ fontSize: 9, color: '#b45309', whiteSpace: 'nowrap' }}>
                              from {formatDay(task.target_date?.slice(0, 10))}
                            </span>
                            <div style={{ flex: 1 }} />
                            {notes[String(task.id)] ? (
                              <FileText size={10} color="var(--orange)" style={{ flexShrink: 0 }} />
                            ) : null}
                            <button onClick={() => moveTaskToDay(task.id, today)} title="Move to today"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--navy)', padding: 0, display: 'flex' }}>
                              <ArrowRight size={12} />
                            </button>
                            <button onClick={() => weeklyToBacklog(task.id)} title="Move to Backlog"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b45309', padding: 0, display: 'flex' }}>
                              <Archive size={12} />
                            </button>
                            <button onClick={() => removeWeekly(task.id)} title="Delete"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 0, display: 'flex' }}>
                              <X size={12} />
                            </button>
                          </div>
                          <div onClick={() => openDetail(task)} title="Open details"
                            style={{ color: 'var(--gray-800)', overflowWrap: 'anywhere', cursor: 'pointer', lineHeight: 1.35 }}>
                            {task.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {weekDays.map(day => {
                  const isToday = day === today;
                  const tasks = weeklyTasks.filter(t => t.target_date?.slice(0, 10) === day);
                  const done = tasks.filter(t => t.completed).length;
                  const dayEvents = calEvents.filter(evt => getEventDate(evt.startTime) === day && !dismissedEvents.includes(evt.id) && !HIDDEN_EVENTS.includes(evt.subject?.toLowerCase()));

                  // Build combined list: interleave calendar events and tasks by sort position
                  const combined = [
                    ...dayEvents.map(evt => ({ kind: 'event', event: evt, sortPos: evt.isAllDay ? -1 : getEventMinutes(evt.startTime), id: 'evt-' + evt.id })),
                    ...tasks.map(t => ({ kind: 'task', task: t, sortPos: t.sort_order ?? 2400, id: 'task-' + t.id })),
                  ].sort((a, b) => a.sortPos - b.sortPos);

                  return (
                    <div key={day} style={{ background: isToday ? '#e8f0fe' : 'var(--gray-50)', borderRadius: 8, border: isToday ? '2px solid var(--navy)' : '1px solid var(--gray-200)', padding: 10, minHeight: 150, minWidth: 0, overflow: 'hidden' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: isToday ? 'var(--navy)' : 'var(--gray-600)', marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                        <span>{formatDay(day)}</span>
                        {tasks.length > 0 && <span style={{ fontSize: 10, color: done === tasks.length ? 'var(--green)' : 'var(--gray-400)' }}>{done}/{tasks.length}</span>}
                      </div>
                      {combined.map((item, cIdx) => item.kind === 'event' ? (
                        <div key={item.id} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 5, marginBottom: 4, fontSize: 10,
                          padding: '4px 6px', background: '#f0f4ff', borderRadius: 4, border: '1px solid #d0daf0',
                          color: '#4a5568',
                        }}>
                          <Calendar size={10} style={{ flexShrink: 0, marginTop: 1.5 }} color="#0078d4" />
                          <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.35 }}>
                            <div style={{ fontWeight: 600, color: '#2d3748' }}>{item.event.subject}</div>
                            <div style={{ color: '#718096' }}>
                              {item.event.isAllDay ? 'All day' : `${formatEventTime(item.event.startTime)} – ${formatEventTime(item.event.endTime)}`}
                            </div>
                            {(() => {
                              const loc = splitLocation(item.event.location);
                              if (!loc) return null;
                              return (
                                <div style={{ color: '#a0aec0', fontSize: 9, display: 'flex', gap: 4, alignItems: 'baseline', flexWrap: 'wrap' }}>
                                  {loc.text && <span>{loc.text}</span>}
                                  {loc.url && (
                                    <a href={loc.url} target="_blank" rel="noreferrer" title={loc.url}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: '#0078d4', fontWeight: 600, textDecoration: 'none' }}>Join</a>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                          <button onClick={() => dismissEvent(item.event.id)} title="Dismiss"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a0aec0', padding: 0, display: 'flex', flexShrink: 0, marginTop: 1 }}>
                            <Check size={10} />
                          </button>
                        </div>
                      ) : (
                        <div key={item.id} style={{
                          marginBottom: 5, fontSize: 11, opacity: isMineToDo(item.task || {}) ? 1 : 0.6,
                          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 5, padding: '4px 5px',
                        }}>
                          {/* Controls first, on their own line. Sharing a row with the label in a
                              ~130px column left roughly one word per line. */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                            <button onClick={() => toggleWeekly(item.task.id, item.task.completed)} title="Mark done" style={{
                              width: 15, height: 15, borderRadius: 3, border: item.task.completed ? 'none' : '1.5px solid var(--gray-400)',
                              background: item.task.completed ? 'var(--green)' : 'transparent', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>
                              {item.task.completed && <Check size={10} color="#fff" strokeWidth={3} />}
                            </button>
                            <button onClick={() => reorderWeekly(item.task.id, 'up', combined)} disabled={cIdx === 0} title="Move up"
                              style={{ background: 'none', border: 'none', cursor: cIdx === 0 ? 'default' : 'pointer', color: cIdx === 0 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, lineHeight: 1, display: 'flex' }}>
                              <ChevronUp size={12} />
                            </button>
                            <button onClick={() => reorderWeekly(item.task.id, 'down', combined)} disabled={cIdx === combined.length - 1} title="Move down"
                              style={{ background: 'none', border: 'none', cursor: cIdx === combined.length - 1 ? 'default' : 'pointer', color: cIdx === combined.length - 1 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, lineHeight: 1, display: 'flex' }}>
                              <ChevronDown size={12} />
                            </button>
                            <div style={{ flex: 1 }} />
                            {notes[String(item.task.id)] ? (
                              <FileText size={10} color="var(--orange)" style={{ flexShrink: 0 }} />
                            ) : null}
                            {weekDays.indexOf(day) < 6 && !item.task.completed && (
                              <button onClick={() => moveWeekly(item.task.id, weekDays[weekDays.indexOf(day) + 1])} title="Move to next day"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 0, display: 'flex' }}>
                                <ArrowRight size={12} />
                              </button>
                            )}
                            {!item.task.completed && (
                              <button onClick={() => weeklyToBacklog(item.task.id)} title="Send to Backlog"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b45309', padding: 0, display: 'flex' }}>
                                <Archive size={12} />
                              </button>
                            )}
                            <button onClick={() => removeWeekly(item.task.id)} title="Delete"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 0, display: 'flex' }}>
                              <X size={12} />
                            </button>
                          </div>
                          <div onClick={() => openDetail(item.task)} title="Open details"
                            style={{
                              overflowWrap: 'anywhere', cursor: 'pointer', lineHeight: 1.35,
                              color: item.task.completed ? 'var(--gray-400)' : 'var(--gray-800)',
                              textDecoration: item.task.completed ? 'line-through' : 'none',
                            }}>
                            {item.task.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══════ TASK DETAIL OVERLAY ═══════ */}
        {detailTask && (
          <div
            onClick={() => closeDetail(true)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,57,92,0.45)', zIndex: 1200,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
            }}>
            <div onClick={e => e.stopPropagation()} style={{
              background: '#fff', borderRadius: 12, width: 'min(680px, 100%)', maxHeight: '85vh',
              display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.28)', overflow: 'hidden',
            }}>
              <div style={{ background: 'var(--navy)', color: '#fff', padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', opacity: 0.75, marginBottom: 3 }}>
                    {formatDay(detailTask.target_date?.slice(0, 10))}
                    {detailTask.completed ? ' · done' : ''}
                  </div>
                  <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.35, overflowWrap: 'anywhere' }}>{detailTask.text}</div>
                </div>
                <button onClick={() => closeDetail(true)} title="Close (saves the note)"
                  style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: 2, opacity: 0.85, flexShrink: 0 }}>
                  <X size={18} />
                </button>
              </div>

              <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', marginBottom: 6 }}>NOTES</div>
                <textarea
                  value={detailDraft}
                  onChange={e => setDetailDraft(e.target.value)}
                  placeholder="Everything you need to pick this up cold. Saves when you close."
                  style={{
                    width: '100%', minHeight: 260, resize: 'vertical', padding: 12,
                    border: '1px solid var(--gray-300)', borderRadius: 8, fontSize: 12.5, lineHeight: 1.6,
                    fontFamily: 'inherit', color: 'var(--gray-800)', boxSizing: 'border-box', whiteSpace: 'pre-wrap',
                  }} />
              </div>

              <div style={{ borderTop: '1px solid var(--gray-200)', padding: '12px 18px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                {!detailTask.completed && (
                  <button onClick={async () => { await closeDetail(true); toggleWeekly(detailTask.id, false); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                    <Check size={13} /> Mark done
                  </button>
                )}
                <select value="" onChange={async e => { const d = e.target.value; if (!d) return; await closeDetail(true); moveTaskToDay(detailTask.id, d); }}
                  style={{ border: '1px solid var(--gray-300)', borderRadius: 6, padding: '7px 8px', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-700)' }}>
                  <option value="">Move to...</option>
                  {scheduleDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
                </select>
                <button onClick={async () => { await closeDetail(true); weeklyToBacklog(detailTask.id); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#fff', color: '#b45309', border: '1px solid #f59e0b', borderRadius: 6, padding: '7px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  <Archive size={13} /> Backlog
                </button>
                <div style={{ flex: 1 }} />
                <button onClick={() => closeDetail(true)}
                  style={{ background: 'var(--navy)', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Save and close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══════ PROJECTS ═══════ */}
        {activeTab === 'projects' && (
          <TodoProjects
            data={projectsData}
            reload={loadProjects}
            today={today}
            scheduleDays={scheduleDays}
            formatDay={formatDay}
            onScheduled={loadWeekly}
          />
        )}

        {/* ═══════ BACKLOG ═══════ */}
        {activeTab === 'backlog' && (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <div style={{ position: 'relative', flex: 1 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: 'var(--gray-400)' }} />
                <input type="text" value={backlogSearch} onChange={e => { setBacklogSearch(e.target.value); setBacklogLoading(true); }}
                  placeholder="Search backlog..." style={{ ...INPUT_STYLE, paddingLeft: 30, width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input type="text" value={newBacklog} onChange={e => setNewBacklog(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addBacklog()}
                placeholder="Add to backlog..." style={INPUT_STYLE} />
              <button onClick={addBacklog} disabled={!newBacklog.trim()} style={BTN_STYLE('var(--navy)', !newBacklog.trim())}>
                <Plus size={14} /> Add
              </button>
            </div>

            {backlogLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={14} className="spin" /> Loading...</div>
            ) : backlog.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)', fontSize: 12.5 }}>
                {backlogSearch ? 'No matching items.' : 'Backlog is empty — add tasks above.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {backlog.filter(item => !onBoard('greg_backlog', item.id)).map((item, idx) => (
                  <div key={item.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                    background: 'var(--gray-50)', borderRadius: 6, border: '1px solid var(--gray-200)', fontSize: 12.5,
                  }}>
                    <span style={{ fontSize: 10, color: 'var(--gray-400)', fontWeight: 700, width: 20, textAlign: 'center' }}>{idx + 1}</span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flexShrink: 0 }}>
                      <button onClick={() => reorderBacklog(item.id, 'up')} disabled={idx === 0}
                        style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, lineHeight: 1 }}>
                        <ChevronUp size={13} />
                      </button>
                      <button onClick={() => reorderBacklog(item.id, 'down')} disabled={idx === backlog.length - 1}
                        style={{ background: 'none', border: 'none', cursor: idx === backlog.length - 1 ? 'default' : 'pointer', color: idx === backlog.length - 1 ? 'var(--gray-300)' : 'var(--gray-500)', padding: 0, lineHeight: 1 }}>
                        <ChevronDown size={13} />
                      </button>
                    </div>
                    <span style={{ flex: 1, color: 'var(--gray-900)' }}>{item.text}</span>
                    {projectsData.projects.length > 0 && (
                      <select onChange={e => { if (e.target.value) backlogToProject(item.id, parseInt(e.target.value)); e.target.value = ''; }}
                        defaultValue="" title="File into a project"
                        style={{ fontSize: 10, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)', maxWidth: 130 }}>
                        <option value="" disabled>→ Project</option>
                        {projectsData.projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    )}
                    <select onChange={e => { if (e.target.value) backlogToWeekly(item.id, e.target.value); e.target.value = ''; }}
                      defaultValue="" style={{ fontSize: 10, border: '1px solid var(--gray-300)', borderRadius: 4, padding: '2px 4px', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--gray-600)' }}>
                      <option value="" disabled>Schedule →</option>
                      {scheduleDays.map(d => <option key={d} value={d}>{formatDay(d)}</option>)}
                    </select>
                    <button onClick={() => removeBacklog(item.id)} title="Done / Remove" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--green)', padding: 2 }}>
                      <CheckCircle size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
